import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { Box, ButtonBase, Typography } from "@mui/material";

import { ProgressOverlay } from "@framework/mui-page-layout";
import { useApiCall } from "@framework/react-hooks";
import { useUser } from "@framework/provider-user";
import type { IUser } from "@framework/types";

import { useSpeed } from "../../../../../../context/speed-context";
import type { IMultiAnalysisToolbarState } from "../../../multi-analysis/types";
import { isVibrationUnitCode } from "../../../multi-analysis/utils/signal-unit-conversion";
import type { ISignalDataResponse } from "../../../scheme/components/analytics/interfaces";
import { createStableColorAssigner } from "../../../scheme/components/analytics/utils/line-colors";
import { formatMeasurementNumber } from "../../../scheme/utils/measurement-format";
import type { ISpeedScreenFormContentProps } from "../types";
import {
  buildCascadeSpectra,
  buildCascadeTrendSeries,
  CASCADE_ROTATION_FREQUENCY_UNIT_CODE,
  formatCascadeHeader,
  formatCascadeUnitLabel,
  getCascadeFrameMarks,
  chunkCascadeItems,
  resolveCascadeCursor,
  sortCascadeSpectra,
  type ICascadeDataMark,
} from "./model";
import {
  applyCascadeFrequencyProfile,
  cascadeDisplayFrequency,
  convertCascadeTrendSeries,
  sampleCascadeSpectrumAmplitude,
  scaleCascadeSpectrumFrequency,
} from "./tools";
import { CascadeTrendChart } from "./trend-chart";
import { CascadeWaterfallChart } from "./waterfall-chart";

export const CascadeScreenForm: FC<ISpeedScreenFormContentProps> = ({ windowId }) => {
  const { formatMessage } = useIntl();
  const { profile } = useUser<IUser>();
  const {
    selectedPoints,
    dataMarks,
    selectedDataMarks,
    frameRange,
    dataMarksError,
    isDataMarksLoading,
    toolbarState,
    setToolbarState,
  } = useSpeed();
  const toolbarRef = useRef(toolbarState);
  toolbarRef.current = toolbarState;
  const colors = useRef(createStableColorAssigner());
  const [cursorKey, setCursorKey] = useState<string | null>(null);
  const [cursorAmplitude, setCursorAmplitude] = useState<number | null>(null);
  const [hiddenSeriesKeys, setHiddenSeriesKeys] = useState<string[]>([]);
  const hiddenSeries = useMemo(() => new Set(hiddenSeriesKeys), [hiddenSeriesKeys]);
  const toggleSeries = useCallback((key: string) => {
    setHiddenSeriesKeys(keys => (keys.includes(key) ? keys.filter(item => item !== key) : [...keys, key]));
  }, []);
  const marks = useMemo(() => getCascadeFrameMarks(dataMarks, frameRange), [dataMarks, frameRange]);
  const series = useMemo(
    () =>
      buildCascadeTrendSeries(
        marks,
        unitCode => formatCascadeUnitLabel(formatMessage({ id: `enums.units.${unitCode}` })),
        key => colors.current.get(key),
      ),
    [formatMessage, marks],
  );
  const visibleSeries = useMemo(() => series.filter(item => !hiddenSeries.has(item.key)), [hiddenSeries, series]);
  const seriesUnitsKey = series.map(item => `${item.key}:${item.unitCode}`).join("|");
  const syncedUnitsKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!series.length) return;
    const nativeCode = series.map(item => item.unitCode).find(isVibrationUnitCode);
    const patch: Partial<IMultiAnalysisToolbarState> = {};
    if (toolbarRef.current.cascadeCanConvertUnits !== (nativeCode != null)) {
      patch.cascadeCanConvertUnits = nativeCode != null;
    }
    if (seriesUnitsKey !== syncedUnitsKeyRef.current) {
      syncedUnitsKeyRef.current = seriesUnitsKey;
      if (nativeCode != null && toolbarRef.current.signalDisplayUnits !== nativeCode) {
        patch.signalDisplayUnits = nativeCode;
      }
    }
    if (Object.keys(patch).length) setToolbarState(patch);
  }, [series, seriesUnitsKey, setToolbarState]);
  const visibleSampleKeys = useMemo(
    () => new Set(visibleSeries.flatMap(item => item.samples.map(sample => sample.key))),
    [visibleSeries],
  );
  const visibleMarks = useMemo(() => marks.filter(mark => visibleSampleKeys.has(mark.key)), [marks, visibleSampleKeys]);
  const selectedMarkKey = selectedDataMarks[selectedDataMarks.length - 1]?.key ?? null;
  useEffect(() => {
    if (selectedMarkKey) {
      setCursorKey(selectedMarkKey);
      setCursorAmplitude(null);
    }
  }, [selectedMarkKey]);
  const cursor = resolveCascadeCursor(visibleMarks, cursorKey);
  const formatValue = useCallback(
    (value: number, unitCode: number | null) =>
      formatMeasurementNumber(value, unitCode, profile.interfaceSettings.dataFormat, { fallbackDigits: 2 }),
    [profile.interfaceSettings.dataFormat],
  );
  const activeSeriesKey = cursor
    ? `${cursor.idPoint}:${cursor.idMeasure}:${cursor.units}`
    : visibleSeries[0]?.key ?? null;
  const spectrumMarks = useMemo(
    () => visibleMarks.filter(mark => `${mark.idPoint}:${mark.idMeasure}:${mark.units}` === activeSeriesKey),
    [activeSeriesKey, visibleMarks],
  );
  const requestKey = spectrumMarks.map(mark => `${mark.idMeasure}:${mark.date}`).join(";");
  const [loaded, setLoaded] = useState<{ key: string; signals: ISignalDataResponse[]; error: boolean } | null>(null);
  const requestGenerationRef = useRef(0);
  const { fn: loadSpectra } = useApiCall(
    async (api, requestMarks: ICascadeDataMark[]) => {
      const seen = new Set<string>();
      const refs = requestMarks.flatMap(mark => {
        const key = `${mark.idMeasure}@${mark.date}`;
        if (seen.has(key)) return [];
        seen.add(key);
        return [{ idMeasure: mark.idMeasure, date: mark.date }];
      });
      if (!refs.length) return [];
      const generation = requestGenerationRef.current;
      const signals: ISignalDataResponse[] = [];
      for (const measures of chunkCascadeItems(refs)) {
        if (generation !== requestGenerationRef.current) return signals;
        const response = (await api.fetchJson({
          url: "/point-v2/measure/signal/data",
          method: "POST",
          data: { measures },
        })) as ISignalDataResponse[] | null;
        if (Array.isArray(response)) signals.push(...response);
      }
      return signals;
    },
    { success: false, error: false },
  );
  const loadSpectraRef = useRef(loadSpectra);
  const spectrumMarksRef = useRef(spectrumMarks);
  spectrumMarksRef.current = spectrumMarks;
  useEffect(() => {
    loadSpectraRef.current = loadSpectra;
  }, [loadSpectra]);
  useEffect(() => {
    if (!requestKey) {
      setLoaded(null);
      return;
    }
    const generation = ++requestGenerationRef.current;
    void loadSpectraRef
      .current(undefined, spectrumMarksRef.current)
      .then(signals => {
        if (generation === requestGenerationRef.current) {
          setLoaded({ key: requestKey, signals: signals ?? [], error: false });
        }
      })
      .catch(() => {
        if (generation === requestGenerationRef.current) {
          setLoaded({ key: requestKey, signals: [], error: true });
        }
      });
  }, [requestKey]);
  const spectraSettled = requestKey !== null && loaded?.key === requestKey;
  const isSpectraLoading = requestKey !== null && !spectraSettled;
  const unitLabel = useCallback(
    (unitCode: number) => formatCascadeUnitLabel(formatMessage({ id: `enums.units.${unitCode}` })),
    [formatMessage],
  );
  const spectra = useMemo(() => {
    if (!spectraSettled || !loaded || loaded.error) return [];
    return sortCascadeSpectra(
      buildCascadeSpectra(spectrumMarks, loaded.signals, visibleSeries, unitLabel, toolbarState.signalDisplayUnits).map(
        spectrum => scaleCascadeSpectrumFrequency(spectrum, toolbarState.spectrumFrequencyScale, unitLabel),
      ),
      toolbarState.cascadeSortMode,
    );
  }, [
    loaded,
    spectraSettled,
    spectrumMarks,
    toolbarState.cascadeSortMode,
    toolbarState.signalDisplayUnits,
    toolbarState.spectrumFrequencyScale,
    unitLabel,
    visibleSeries,
  ]);
  const showTrend = toolbarState.cascadeShowTrend;
  const profileSelection = showTrend && toolbarState.frequencyProfile ? toolbarState.frequencyProfileSelection : null;
  const convertedSeries = useMemo(
    () => convertCascadeTrendSeries(series, toolbarState.signalDisplayUnits, unitLabel),
    [series, toolbarState.signalDisplayUnits, unitLabel],
  );
  const trendSeries = useMemo(
    () =>
      profileSelection
        ? applyCascadeFrequencyProfile(
            convertedSeries,
            spectra,
            profileSelection.frequencyHz,
            toolbarState.cascadeProfileRpm,
          )
        : convertedSeries,
    [convertedSeries, profileSelection, spectra, toolbarState.cascadeProfileRpm],
  );
  const handleCursorChange = useCallback(
    (key: string, amplitude?: number, frequencyHz?: number | null, rpm?: number | null) => {
      setCursorKey(key);
      setCursorAmplitude(amplitude === undefined ? null : amplitude);
      if (frequencyHz == null) return;
      const tools = toolbarRef.current;
      const patch: Partial<IMultiAnalysisToolbarState> = {};
      if (tools.markerMode) {
        patch.cascadeMarkerFrequencyHz = frequencyHz;
        patch.cascadeMarkerRpm = rpm ?? null;
      }
      if (tools.cascadeShowTrend && tools.frequencyProfile) {
        patch.frequencyProfileSelection = { frequencyHz, nativeX: frequencyHz };
        patch.cascadeProfileRpm = rpm ?? null;
      }
      if (Object.keys(patch).length) setToolbarState(patch);
    },
    [setToolbarState],
  );
  const cursorSpectrum = cursor ? spectra.find(spectrum => spectrum.key === cursor.key) ?? null : null;
  const markerReadout = useMemo(() => {
    if (!toolbarState.markerMode || toolbarState.cascadeMarkerFrequencyHz == null || !spectra.length) return null;
    const spectrum = cursorSpectrum ?? spectra[spectra.length - 1];
    const displayFrequency = cascadeDisplayFrequency(
      toolbarState.cascadeMarkerFrequencyHz,
      spectrum.frequencyUnitCode,
      toolbarState.cascadeMarkerRpm,
    );
    if (displayFrequency == null) return null;
    const amplitude = sampleCascadeSpectrumAmplitude(
      spectrum,
      toolbarState.cascadeMarkerFrequencyHz,
      toolbarState.cascadeMarkerRpm,
    );
    const frequencyLabel = formatMessage({ id: "pages.multiAnalysis.toolbar.markerFrequency" });
    const amplitudeLabel = formatMessage({ id: "pages.multiAnalysis.toolbar.markerAmplitude" });
    const frequencyLine = `${frequencyLabel}: ${formatValue(displayFrequency, spectrum.frequencyUnitCode)} ${
      spectrum.frequencyUnits
    }`.trim();
    if (amplitude == null) return [frequencyLine];
    return [frequencyLine, `${amplitudeLabel}: ${formatValue(amplitude, spectrum.unitCode)} ${spectrum.units}`.trim()];
  }, [
    cursorSpectrum,
    formatMessage,
    formatValue,
    spectra,
    toolbarState.cascadeMarkerFrequencyHz,
    toolbarState.cascadeMarkerRpm,
    toolbarState.markerMode,
  ]);
  const amplitudeUnits =
    cursorAmplitude != null && cursorSpectrum ? cursorSpectrum.units : cursor ? unitLabel(cursor.units) : "";
  const header = cursor
    ? formatCascadeHeader(
        cursor,
        amplitudeUnits,
        unitLabel(CASCADE_ROTATION_FREQUENCY_UNIT_CODE),
        formatValue,
        cursorAmplitude,
        cursorAmplitude != null && cursorSpectrum ? cursorSpectrum.unitCode : cursor.units,
      )
    : null;
  const placeholder = !selectedPoints.length
    ? "pages.multiAnalysis.chartPlaceholder"
    : dataMarksError
      ? "pages.speed.cascade.loadError"
      : "pages.multiAnalysis.chartEmpty";

  return (
    <Box
      data-testid={`speed-cascade-${windowId}`}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {isDataMarksLoading ? <ProgressOverlay isLoading /> : null}
      {series.length ? (
        <Box sx={{ flex: 1, minHeight: 0, overflowX: "hidden", overflowY: "auto" }}>
          <Box sx={{ minHeight: "100%", display: "flex", flexDirection: "column" }}>
            {showTrend ? (
              <Box sx={{ px: 1, py: 0.5, display: "flex", gap: 1.5, flexWrap: "wrap", flexShrink: 0 }}>
                {series.map(item => (
                  <ButtonBase
                    key={item.key}
                    aria-pressed={!hiddenSeries.has(item.key)}
                    onClick={() => toggleSeries(item.key)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      minWidth: 0,
                      borderRadius: 0.5,
                      px: 0.5,
                      py: 0.25,
                      opacity: hiddenSeries.has(item.key) ? 0.5 : 1,
                      "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 1 },
                    }}
                  >
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: item.color, flexShrink: 0 }} />
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "text.secondary",
                        overflowWrap: "anywhere",
                        textDecoration: hiddenSeries.has(item.key) ? "line-through" : "none",
                      }}
                    >
                      {item.name}
                    </Typography>
                  </ButtonBase>
                ))}
              </Box>
            ) : null}
            <Box
              data-cascade-show-trend={showTrend ? "on" : "off"}
              sx={{
                flex: "1 0 0",
                minHeight: showTrend ? 480 : 280,
                overflow: "hidden",
                display: "grid",
                gridTemplateRows: showTrend ? "minmax(0, 1fr) minmax(0, 1fr)" : "minmax(0, 1fr)",
              }}
            >
              {(["trend", "waterfall"] as const)
                .filter(paneId => showTrend || paneId === "waterfall")
                .map(paneId => (
                  <Box
                    key={paneId}
                    sx={{
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                      borderBottom: paneId === "trend" ? 1 : 0,
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      title={header ?? undefined}
                      aria-live="polite"
                      sx={{
                        px: 1,
                        py: 0.5,
                        fontSize: 12,
                        fontWeight: 500,
                        overflowWrap: "anywhere",
                        flexShrink: 0,
                      }}
                    >
                      {header}
                    </Typography>
                    {paneId === "trend" ? (
                      <CascadeTrendChart
                        series={trendSeries}
                        cursorKey={cursor?.key ?? null}
                        onCursorChange={handleCursorChange}
                        formatValue={formatValue}
                        sortMode={toolbarState.cascadeSortMode}
                        markerMode={toolbarState.markerMode}
                        hiddenSeriesKeys={hiddenSeriesKeys}
                        axisUnitCode={trendSeries[0]?.unitCode}
                        axisUnits={trendSeries[0]?.units}
                      />
                    ) : isSpectraLoading ? (
                      <Box sx={{ flex: 1, position: "relative" }}>
                        <ProgressOverlay isLoading />
                      </Box>
                    ) : loaded?.error ? (
                      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
                        <Typography color="error" sx={{ fontSize: 13 }} align="center">
                          {formatMessage({ id: "pages.speed.cascade.spectrumLoadError" })}
                        </Typography>
                      </Box>
                    ) : spectra.length ? (
                      <>
                        {markerReadout ? (
                          <Typography
                            data-cascade-marker-readout=""
                            aria-live="polite"
                            sx={{ px: 1, pb: 0.5, fontSize: 12, flexShrink: 0 }}
                          >
                            {markerReadout.join(" · ")}
                          </Typography>
                        ) : null}
                        <CascadeWaterfallChart
                          spectra={spectra}
                          cursorKey={cursor?.key ?? null}
                          onCursorChange={handleCursorChange}
                          formatValue={formatValue}
                          sortMode={toolbarState.cascadeSortMode}
                          display3D={toolbarState.cascadeDisplay3D}
                          hiddenSeriesKeys={hiddenSeriesKeys}
                          markerMode={toolbarState.markerMode}
                          markerFrequencyHz={toolbarState.markerMode ? toolbarState.cascadeMarkerFrequencyHz : null}
                          markerRpm={toolbarState.cascadeMarkerRpm}
                          harmonicCursors={toolbarState.harmonicCursors}
                          subHarmonicCursors={toolbarState.subHarmonicCursors}
                          frequencyProfile={toolbarState.frequencyProfile && showTrend}
                          profileFrequencyHz={profileSelection?.frequencyHz ?? null}
                          profileRpm={toolbarState.cascadeProfileRpm}
                        />
                      </>
                    ) : (
                      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
                        <Typography color="textSecondary" sx={{ fontSize: 13 }} align="center">
                          {formatMessage({ id: "pages.multiAnalysis.chartEmpty" })}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ))}
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
          {!isDataMarksLoading ? (
            <Typography color={dataMarksError ? "error" : "textSecondary"} sx={{ fontSize: 13 }} align="center">
              {formatMessage({ id: placeholder })}
            </Typography>
          ) : null}
        </Box>
      )}
    </Box>
  );
};
